type UploadedRowsPayload = {
  rowsStorageKey: string;
  rowsFormat: "json" | "json.gz";
  rowCount: number;
};

type UploadParams = {
  generateUploadUrl: () => Promise<{ key: string; url: string }>;
  headers: string[];
  rows: string[][];
};

async function buildRowsBlob(
  headers: string[],
  rows: string[][]
): Promise<{ blob: Blob; rowsFormat: "json" | "json.gz"; contentType: string }> {
  const json = JSON.stringify({ headers, rows });
  const plainBlob = new Blob([json], { type: "application/json" });

  if (typeof CompressionStream === "undefined") {
    return {
      blob: plainBlob,
      rowsFormat: "json",
      contentType: "application/json",
    };
  }

  try {
    const compressedStream = plainBlob
      .stream()
      .pipeThrough(new CompressionStream("gzip"));
    const compressedBlob = await new Response(compressedStream).blob();
    return {
      blob: compressedBlob,
      rowsFormat: "json.gz",
      contentType: "application/json+gzip",
    };
  } catch {
    return {
      blob: plainBlob,
      rowsFormat: "json",
      contentType: "application/json",
    };
  }
}

export async function uploadTableRowsPayload({
  generateUploadUrl,
  headers,
  rows,
}: UploadParams): Promise<UploadedRowsPayload> {
  const { key, url } = await generateUploadUrl();
  const { blob, rowsFormat, contentType } = await buildRowsBlob(headers, rows);

  const response = await fetch(url, {
    method: "PUT",
    body: blob,
    headers: { "Content-Type": contentType },
  });
  if (!response.ok) {
    throw new Error(`Row payload upload failed: ${response.status}`);
  }

  return {
    rowsStorageKey: key,
    rowsFormat,
    rowCount: rows.length,
  };
}
